import React, { useEffect, useMemo, useState } from "react";
import "./Dashboard.css";
import owlyLogo from "../../assets/owly-logo.png";
import { useTranslation } from "react-i18next";
import useUser from "../../../hooks/useUser";
import PricingCard from "./PricingCard";
import ReviewChangesModal from "../Suscriptions/ReviewChangesModal/ReviewChangesModal";
import {
  createSubscriptionCheckout,
  getSubscriptionProductsDropdown,
  getOrganizationSubscription,
  changeOrganizationPlan,
} from "../../../services/subscriptionsService";

const isSubscriptionCancelled = (sub) => {
  if (!sub) return false;
  const status = (sub.status || "").toLowerCase();
  const stripeStatus = (sub.stripe_subscription?.status || "").toLowerCase();
  const isActiveFlag = sub.is_active;
  return (
    status === "canceled" ||
    status === "cancelled" ||
    stripeStatus === "canceled" ||
    stripeStatus === "cancelled" ||
    isActiveFlag === false
  );
};

// Devuelve true si la suscripción está cancelada o tiene cancelación programada
// al final del período (cancel_at_period_end), igual que en la página de Suscripciones.
const isSubscriptionCancellationLocked = (subscription) => {
  if (!subscription) return false;

  const normalizeBool = (val) =>
    val === true || val === 1 || val === "1" || val === "true";

  const cancelAtPeriodEnd =
    normalizeBool(subscription.cancel_at_period_end) ||
    normalizeBool(subscription.stripe_subscription?.cancel_at_period_end);

  return cancelAtPeriodEnd || isSubscriptionCancelled(subscription);
};

export default function Dashboard() {
  const { user } = useUser();
  const { t } = useTranslation("dashboard");
  const { t: tSubs } = useTranslation("suscriptions");

  const organizationId = useMemo(() => {
    const org = user?.organization || user?.organization_id;
    if (!org) return null;
    if (typeof org === "object") {
      return org.id || org._id || null;
    }
    return org;
  }, [user?.organization, user?.organization_id]);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [productsRaw, setProductsRaw] = useState([]);
  const [currentPlanStripeId, setCurrentPlanStripeId] = useState(null);
  const [currentPlanBillingCycle, setCurrentPlanBillingCycle] = useState("monthly");
  const [refreshTick, setRefreshTick] = useState(0);
  const [subscriptionCancellationLocked, setSubscriptionCancellationLocked] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(null);
  const [pendingBilling, setPendingBilling] = useState("monthly");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  const handleCheckout = async ({ planId, billingCycle = "monthly" }) => {
    let shouldCloseModal = true;
    try {
      if (!planId) return;
      setCheckoutLoading(true);
      setCheckoutError(null);

      // Si no tenemos organización, siempre creamos una nueva suscripción.
      if (!organizationId) {
        try {
          const data = await createSubscriptionCheckout({
            planStripeId: planId,
            billingCycle,
          });
          const url = data.checkout_url || data.checkoutUrl || data.url;
          if (url) {
            window.location.href = url;
          }
        } catch (e) {
          console.error("Checkout create failed (dashboard)", {
            planStripeId: planId,
            billingCycle,
            response: e?.response,
            error: e,
          });
          setCheckoutError(e?.response?.data?.error || tSubs("page.errorCheckout"));
          shouldCloseModal = false;
        }
        if (shouldCloseModal) setPendingCheckout(null);
        return;
      }

      // Verificamos si ya existe una suscripción activa para la organización.
      let subscription = null;
      let hasActiveSubscription = false;
      try {
        subscription = await getOrganizationSubscription(organizationId);

        // Si la suscripción está cancelada o tiene cancelación programada,
        // la tratamos como "sin suscripción" para forzar un nuevo checkout.
        const locked = isSubscriptionCancellationLocked(subscription);
        if (locked || isSubscriptionCancelled(subscription)) {
          subscription = null;
        }
        hasActiveSubscription = !!subscription;
      } catch (e) {
        const status = e?.response?.status;
        if (status && status !== 404) {
          console.error("Error fetching organization subscription from dashboard", e?.response || e);
        }
      }

      if (hasActiveSubscription && Array.isArray(productsRaw) && productsRaw.length > 0) {
        const subName = subscription?.subscription_name || subscription?.name;
        const subProductId =
          subscription?.stripe_product_id ||
          subscription?.product_stripe_id ||
          subscription?.stripe_product ||
          subscription?.product_id ||
          subscription?.product?.stripe_product_id ||
          null;

        const normalizeCycle = (value) =>
          value === "year" || value === "yearly" ? "yearly" : "monthly";

        const currentBilling = normalizeCycle(
          subscription?.current_plan?.billing_cycle ||
            subscription?.billing_cycle ||
            subscription?.plan?.billing_cycle ||
            "monthly"
        );

        let currentProduct = productsRaw.find((p) => {
          const pid =
            p.stripe_product_id ||
            p.product_stripe_id ||
            p.stripe_product ||
            p.product_id ||
            null;
          return subProductId && pid && pid === subProductId;
        });

        if (!currentProduct && subName) {
          currentProduct = productsRaw.find((p) => {
            const name = p.subscription_name || p.name;
            return name === subName;
          });
        }

        const plansArray = Array.isArray(currentProduct?.plans)
          ? currentProduct.plans
          : [];

        const monthlyPlanFromProduct =
          plansArray.find((p) => p?.billing_cycle === "month" || p?.cycle === "monthly") ||
          null;
        const yearlyPlanFromProduct =
          plansArray.find((p) => p?.billing_cycle === "year" || p?.cycle === "yearly") ||
          null;

        const monthlyPriceIdFromProduct =
          monthlyPlanFromProduct?.stripe_id ||
          monthlyPlanFromProduct?.id ||
          monthlyPlanFromProduct?.price_stripe_id ||
          null;
        const yearlyPriceIdFromProduct =
          yearlyPlanFromProduct?.stripe_id ||
          yearlyPlanFromProduct?.id ||
          yearlyPlanFromProduct?.price_stripe_id ||
          null;

        // 1) Intentamos usar el plan activo que viene en subscription.plans
        const plansValue =
          subscription?.plans || subscription?.subscription?.plans || null;
        let activePlan = null;
        if (Array.isArray(plansValue)) {
          // Hay backends que retornan más de un plan con active=true.
          // Priorizamos el que coincida con el ciclo real de la suscripción.
          const matchByCycle = plansValue.find((p) => {
            const cycle = normalizeCycle(p?.billing_cycle || p?.cycle);
            return p?.active === true && cycle === currentBilling;
          });

          activePlan =
            matchByCycle ||
            plansValue.find((p) => p?.active === true) ||
            plansValue[0] ||
            null;
        } else if (plansValue && typeof plansValue === "object") {
          activePlan = plansValue;
        }
        const fromPriceIdFromPlans =
          activePlan?.stripe_id ||
          activePlan?.price_stripe_id ||
          activePlan?.id ||
          null;

        // 1b) Fallbacks adicionales: Stripe subscription items y campos antiguos.
        const stripeSub = subscription?.stripe_subscription || null;
        const stripeItems =
          stripeSub?.items?.data ||
          stripeSub?.items ||
          subscription?.items?.data ||
          subscription?.items ||
          [];
        const stripeItem = Array.isArray(stripeItems) ? stripeItems[0] : null;
        const fromPriceIdFromStripeItem =
          stripeItem?.price?.id ||
          stripeItem?.price?.stripe_id ||
          stripeItem?.price_stripe_id ||
          stripeItem?.price_id ||
          null;

        const fromPriceIdFromSubscription =
          subscription?.plan?.stripe_id ||
          subscription?.plan?.id ||
          subscription?.plan?.price_stripe_id ||
          subscription?.stripe_price_id ||
          subscription?.price_stripe_id ||
          null;

        // 2) Fallback: usamos el plan mensual actual del producto
        const fromPriceIdFromProduct = monthlyPriceIdFromProduct || null;

        // Preferimos los IDs de Stripe (item.price.id) y luego el plan activo.
        const fallbackFromPriceId =
          fromPriceIdFromStripeItem ||
          fromPriceIdFromPlans ||
          fromPriceIdFromSubscription ||
          fromPriceIdFromProduct;

        const fallbackToPriceId = planId;

        // Si el usuario está cambiando de ciclo (yearly <-> monthly), resolvemos
        // explícitamente los price ids desde el producto para evitar from==to.
        const requestedBilling = normalizeCycle(billingCycle);
        const isSwitchingCycle = currentBilling !== requestedBilling;

        const resolvedFromPriceId =
          currentBilling === "yearly" ? yearlyPriceIdFromProduct : monthlyPriceIdFromProduct;
        const resolvedToPriceId =
          requestedBilling === "yearly" ? yearlyPriceIdFromProduct : monthlyPriceIdFromProduct;

        const fromPriceId =
          (isSwitchingCycle && resolvedFromPriceId) ? resolvedFromPriceId : fallbackFromPriceId;
        const toPriceId =
          (isSwitchingCycle && resolvedToPriceId) ? resolvedToPriceId : fallbackToPriceId;

        // Si por algún error los ids coinciden, no intentamos actualizar (evita cambios inválidos).
        if (fromPriceId && toPriceId && fromPriceId === toPriceId) {
          console.warn("Skipping plan change because fromPriceId equals toPriceId", {
            fromPriceId,
            toPriceId,
          });
          if (shouldCloseModal) setPendingCheckout(null);
          return;
        }

        if (!fromPriceId || !toPriceId) {
          console.error("Missing price ids for plan change from dashboard", {
            fromPriceId,
            toPriceId,
            monthlyPriceIdFromProduct,
            yearlyPriceIdFromProduct,
          });
        } else {
          const result = await changeOrganizationPlan({
            organizationId,
            fromPriceId,
            toPriceId,
            allowProration: true,
          });

          const url =
            result?.checkout_url || result?.checkoutUrl || result?.url || null;
          if (url) {
            window.location.href = url;
          } else {
            // Si el backend no devuelve URL, al menos actualizamos el plan
            // en memoria para deshabilitar el botón correcto.
            setCurrentPlanStripeId(toPriceId);
            setCurrentPlanBillingCycle(normalizeCycle(billingCycle));

            // Forzamos un refresh para sincronizar estado real (Stripe/backend)
            // y refrescar cards/disabled.
            setRefreshTick((v) => v + 1);
          }
        }
      } else {
        // Sin suscripción activa, creamos un checkout nuevo.
        try {
          const data = await createSubscriptionCheckout({
            planStripeId: planId,
            billingCycle,
          });
          const url = data.checkout_url || data.checkoutUrl || data.url;
          if (url) {
            window.location.href = url;
          }
        } catch (e) {
          console.error("Checkout create failed (dashboard)", {
            planStripeId: planId,
            billingCycle,
            response: e?.response,
            error: e,
          });
          setCheckoutError(e?.response?.data?.error || tSubs("page.errorCheckout"));
          shouldCloseModal = false;
        }
      }
    } catch (e) {
      console.error("Error processing dashboard subscription action", e?.response || e);
      setCheckoutError(e?.response?.data?.error || tSubs("page.errorCheckout"));
      // Mantenemos el modal abierto para mostrar el error.
      // (el estado es async, por eso usamos una bandera local)
      shouldCloseModal = false;
    } finally {
      setCheckoutLoading(false);
      // Si hubo error, dejamos el modal abierto para que se vea el mensaje.
      if (shouldCloseModal) setPendingCheckout(null);
    }
  };

  useEffect(() => {
    if (pendingCheckout) {
      setPendingBilling("monthly");
      setCheckoutError(null);
    }
  }, [pendingCheckout]);

  useEffect(() => {
    const fetchPlans = async () => {
      setPlansLoading(true);
      try {
        const data = await getSubscriptionProductsDropdown();
        if (!Array.isArray(data) || data.length === 0) {
          setPlans([]);
          setProductsRaw([]);
          setCurrentPlanStripeId(null);
          setCurrentPlanBillingCycle("monthly");
          return;
        }

        setProductsRaw(data);

        const parseAmountFromString = (value) => {
          if (!value) return 0;
          if (typeof value === "number") return value;
          const match = String(value).match(/[\d.,]+/);
          if (!match) return 0;
          const normalized = match[0].replace(",", "");
          const parsed = parseFloat(normalized);
          return Number.isNaN(parsed) ? 0 : parsed;
        };

        const normalized = data
          .map((item) => {
            const plansArray = Array.isArray(item.plans) ? item.plans : [];
            const monthlyPlan = plansArray.find(
              (p) => p.billing_cycle === "month" || p.cycle === "monthly"
            );
            const yearlyPlan = plansArray.find(
              (p) => p.billing_cycle === "year" || p.cycle === "yearly"
            );
            if (!monthlyPlan) return null;

            // credits proviene de monthly_usage_limit del backend (por ejemplo "1000.0")
            const credits = parseAmountFromString(
              item.monthly_usage_limit || item.monthlyUsageLimit
            );

            const hasFreeTrial =
              item.have_free_trial === true ||
              item.haveFreeTrial === true ||
              item.has_free_trial === true ||
              item.hasFreeTrial === true ||
              item.have_free_trial === "true" ||
              item.haveFreeTrial === "true" ||
              item.has_free_trial === "true" ||
              item.hasFreeTrial === "true";

            return {
              id: item.id,
              name: item.subscription_name || item.name,
              description: item.description || "",
              hasFreeTrial,
              price: parseAmountFromString(monthlyPlan.amount),
              yearlyPrice: yearlyPlan ? parseAmountFromString(yearlyPlan.amount) : null,
              credits,
              stripeId:
                monthlyPlan.stripe_id ||
                monthlyPlan.id ||
                monthlyPlan.price_stripe_id ||
                null,
              yearlyStripeId:
                yearlyPlan?.stripe_id ||
                yearlyPlan?.id ||
                yearlyPlan?.price_stripe_id ||
                null,
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.price - b.price);

        setPlans(normalized);

        // Intentamos determinar el plan actual usando la suscripción activa.
        if (organizationId) {
          try {
            let subscription = await getOrganizationSubscription(organizationId);

            // Guardamos si la suscripción está cancelada o tiene cancelación programada
            setSubscriptionCancellationLocked(
              isSubscriptionCancellationLocked(subscription)
            );

            if (isSubscriptionCancelled(subscription)) {
              subscription = null;
            }
            if (subscription) {
              const normalizeCycle = (value) =>
                value === "year" || value === "yearly" ? "yearly" : "monthly";

              const subName = subscription?.subscription_name || subscription?.name;
              const subProductId =
                subscription?.stripe_product_id ||
                subscription?.product_stripe_id ||
                subscription?.stripe_product ||
                subscription?.product_id ||
                subscription?.product?.stripe_product_id ||
                null;

              let currentProduct = data.find((p) => {
                const pid =
                  p.stripe_product_id ||
                  p.product_stripe_id ||
                  p.stripe_product ||
                  p.product_id ||
                  null;
                return subProductId && pid && pid === subProductId;
              });

              if (!currentProduct && subName) {
                currentProduct = data.find((p) => {
                  const name = p.subscription_name || p.name;
                  return name === subName;
                });
              }

              const allPlans = Array.isArray(currentProduct?.plans)
                ? currentProduct.plans
                : [];

              // Tomamos el plan activo desde subscription.plans (puede ser objeto o array)
              const plansValue =
                subscription?.plans || subscription?.subscription?.plans || null;
              let activePlan = null;
              if (Array.isArray(plansValue)) {
                activePlan =
                  plansValue.find((p) => p?.active === true) || plansValue[0] || null;
              } else if (plansValue && typeof plansValue === "object") {
                activePlan = plansValue;
              }

              const subscriptionCycle = normalizeCycle(
                activePlan?.billing_cycle ||
                  subscription?.current_plan?.billing_cycle ||
                  subscription?.billing_cycle ||
                  subscription?.plan?.billing_cycle ||
                  currentProduct?.billing_cycle ||
                  currentProduct?.cycle ||
                  "monthly"
              );
              setCurrentPlanBillingCycle(subscriptionCycle);

              let currentStripeId = null;
              if (activePlan?.stripe_id || activePlan?.price_stripe_id || activePlan?.id) {
                currentStripeId =
                  activePlan.stripe_id || activePlan.price_stripe_id || activePlan.id;
              } else {
                // Fallback: si no viene plan activo, usamos el primer plan del producto.
                const fallbackPlan = allPlans[0] || null;
                currentStripeId =
                  fallbackPlan?.stripe_id ||
                  fallbackPlan?.id ||
                  fallbackPlan?.price_stripe_id ||
                  null;
              }

              setCurrentPlanStripeId(currentStripeId || null);
            } else {
              setCurrentPlanStripeId(null);
              setCurrentPlanBillingCycle("monthly");
              setSubscriptionCancellationLocked(false);
            }
          } catch (e) {
            const status = e?.response?.status;
            if (status === 404) {
              setCurrentPlanStripeId(null);
              setCurrentPlanBillingCycle("monthly");
              setSubscriptionCancellationLocked(false);
            } else {
              console.error("Error fetching current subscription for dashboard", e?.response || e);
            }
          }
        }
      } catch (e) {
        console.error("Error loading dashboard pricing plans", e?.response || e);
        setPlans([]);
        setProductsRaw([]);
        setCurrentPlanStripeId(null);
        setCurrentPlanBillingCycle("monthly");
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, [organizationId, refreshTick]);

  // Se mantienen las descripciones/beneficios de i18n, pero el nombre y el precio
  // se toman del backend cuando están disponibles.
  // Si no hay productos desde el backend, no se muestran cards.
  const effectivePlans = useMemo(
    () => (Array.isArray(plans) ? plans : []),
    [plans]
  );

  const getDisplayForPlan = (plan) => {
    if (!plan) {
      return { price: 0, periodLabel: t("pricing.perMonth") };
    }

    const roundCurrency = (value) => {
      if (typeof value !== "number" || Number.isNaN(value)) return 0;
      return Math.round(value * 100) / 100;
    };

    const getMonthlyDisplayPrice = (p) => {
      const yearly = p?.yearlyPrice;
      if (typeof yearly === "number" && yearly > 0) {
        return roundCurrency(yearly / 12);
      }
      return roundCurrency(p?.price ?? 0);
    };

    const isCurrent = !!currentPlanStripeId &&
      (plan.stripeId === currentPlanStripeId ||
        plan.yearlyStripeId === currentPlanStripeId);

    // Si es el plan adquirido y la suscripción está en anual, mostramos anual.
    if (isCurrent && currentPlanBillingCycle === "yearly") {
      return {
        // Mostramos el precio anual (no los créditos)
        price: roundCurrency(plan.yearlyPrice ?? plan.price ?? 0),
        periodLabel: t("pricing.perYear"),
      };
    }

    // En cualquier otro caso, incluído plan adquirido mensual, mostramos mensual por defecto.
    return {
      // Mostramos el precio mensual desde amount
      price: getMonthlyDisplayPrice(plan),
      periodLabel: t("pricing.perMonth"),
    };
  };

  const renderPlanCard = (plan, index) => {
    // Usamos el nombre que viene del backend como título.
    const display = getDisplayForPlan(plan);

    // Determinar si este plan es el actualmente comprado (monthly o yearly)
    const isCurrentMonthly =
      !!currentPlanStripeId &&
      !!plan.stripeId &&
      plan.stripeId === currentPlanStripeId;

    const isCurrentYearly =
      !!currentPlanStripeId &&
      !!plan.yearlyStripeId &&
      plan.yearlyStripeId === currentPlanStripeId;

    const isCurrent = isCurrentMonthly || isCurrentYearly;

    // Solo el plan actual debe tener el estilo "highlighted" (borde violeta).
    const isHighlighted = isCurrent;

    let badgeText;
    if (isCurrent) {
      badgeText = t(
        isCurrentYearly
          ? "pricing.currentYearly"
          : "pricing.currentMonthly"
      );
    }

    // Features de la card:
    // Tomamos la descripción que viene del backend y la separamos
    // en ítems usando saltos de línea, comas y puntos y comas.
    const features = String(plan.description || "")
      .split(/[;,\r?\n]+/)
      .map((item) => item.trim())
      .filter(Boolean);

    return (
      <PricingCard
        key={plan.stripeId || plan.yearlyStripeId || index}
        title={plan.name}
        description={plan.description}
        currency="USD"
        price={display.price}
        period={display.periodLabel}
        features={features}
        ctaLabel={isCurrent ? t("pricing.changeButton") : t("pricing.getButton")}
        highlighted={isHighlighted}
        badgeText={badgeText}
        hasFreeTrial={plan.hasFreeTrial}
        planId={plan.stripeId || plan.yearlyStripeId || `plan-${index}`}
        // Si hay una cancelación programada o ya está cancelada, solo bloqueamos
        // el botón del plan actual (Change). Los otros planes (Get) siguen activos.
        disabled={subscriptionCancellationLocked && isCurrent}
        onCheckout={() => setPendingCheckout({ plan })}
      />
    );
  };

  return (
    <div className="welcome-container">
      <div className="welcome-content">
        {/* Logo de Owly */}
        <div className="welcome-logo">
          <div className="owly-icon-welcome">
            <img src={owlyLogo} alt="Owly Logo" />
          </div>

          {/* Saludo personalizado */}
          <div className="welcome-greeting">
            <h1>{t('greeting', { name: user?.name || user?.username || "Usuario" })}</h1>
            <h2>{t('title')}</h2>
          </div>
        </div>
        {/* Mensaje principal */}
        <div className="welcome-message">
          <p>
            {t('message')}
          </p>
        </div>

        <div className="prices">
          {plansLoading ? (
            <p>{t("pricing.loading")}</p>
          ) : effectivePlans.length === 0 ? (
            <p>{t("pricing.noProducts")}</p>
          ) : (
            <div className="cards-products">
              {effectivePlans.map(renderPlanCard)}
            </div>
          )}

        </div>
      </div>
      <ReviewChangesModal
        open={!!pendingCheckout}
        t={tSubs}
        currentTotal={0}
        mode="create"
        newTotal={
          pendingBilling === "yearly"
            ? pendingCheckout?.plan?.yearlyPrice ?? pendingCheckout?.plan?.price ?? 0
            : pendingCheckout?.plan?.price ?? 0
        }
        billing={pendingBilling}
        showBillingSelector
        billingMonthlyPrice={pendingCheckout?.plan?.price ?? null}
        billingYearlyPrice={pendingCheckout?.plan?.yearlyPrice ?? null}
        billingCurrency="USD"
        billingMonthlyDisabled={
          !!currentPlanStripeId &&
          !!pendingCheckout?.plan &&
          (pendingCheckout.plan.stripeId === currentPlanStripeId ||
            pendingCheckout.plan.yearlyStripeId === currentPlanStripeId) &&
          currentPlanBillingCycle === "monthly"
        }
        billingYearlyDisabled={
          !!currentPlanStripeId &&
          !!pendingCheckout?.plan &&
          (pendingCheckout.plan.stripeId === currentPlanStripeId ||
            pendingCheckout.plan.yearlyStripeId === currentPlanStripeId) &&
          currentPlanBillingCycle === "yearly"
        }
        error={checkoutError}
        onBillingChange={(value) => setPendingBilling(value)}
        loading={checkoutLoading}
        onClose={() => {
          if (checkoutLoading) return;
          setPendingCheckout(null);
        }}
        onConfirm={() => {
          if (!pendingCheckout) return;
          const plan = pendingCheckout?.plan || null;
          const billingCycle = pendingBilling;
          const selectedPlanId =
            billingCycle === "yearly" && plan?.yearlyStripeId
              ? plan.yearlyStripeId
              : plan?.stripeId;
          handleCheckout({ planId: selectedPlanId, billingCycle });
        }}
      />
    </div>
  );
}
